import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Check } from "lucide-react";

/**
 * Lightweight canvas signature pad.
 * Props: onChange(dataUrl|null), height = 160
 */
export default function SignaturePad({ onChange, height = 160, testId = "signature-pad" }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#F26B3A";
  }, [height]);

  const getPos = (e) => {
    const c = ref.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    setDrawing(false);
    setEmpty(false);
    if (onChange) onChange(ref.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div data-testid={testId}>
      <div className="border-2 border-dashed border-gray-300 rounded-md bg-white">
        <canvas
          ref={ref}
          className="w-full block touch-none cursor-crosshair"
          style={{ height }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-gray-500">
          {empty ? "Sign above using your mouse or finger" : "Signature captured"}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={clear} disabled={empty} data-testid="signature-clear">
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}
